import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { withTheme } from '@material-ui/core/styles';
import _ from 'lodash';
import { Element as ScrollElement } from 'react-scroll';
import { MuiThemeProvider } from '@material-ui/core/styles';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import PaddedDivider from 'components/PaddedDivider';
import Container from 'components/Container';
import BlockContentTypography from './BlockContentTypography';
import TimelineContainer from './TimelineContainer';

import { darkTheme, lightTheme } from 'src/theme';

const BoldHeading = styled.div`
  position: relative;
  margin-top: -4rem;
  padding: 2rem 0;
  &:before {
    position: absolute;
    display: block;
    top: 0;
    content: '';
    height: 4px;
    background: linear-gradient(
      ${props => props.theme?.brand?.gradient?.primary}
    );
    width: 100%;
  }
`;

const TimelineHeading = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  top: -4rem;
  margin-bottom: -8rem;
  padding: 2rem 0;
  svg {
    margin: 0 2rem 0 1rem;
  }
  &:before {
    position: absolute;
    display: block;
    top: 0;
    content: '';
    height: 4px;
    background: ${props => props.theme.palette.divider};
    width: 100%;
  }
  @media (min-width: 640px) {
    margin-left: -6rem;
  }
`;

const LabelCircle = styled.div`
  border-radius: 1000em;
  width: 30px;
  height: 30px;
  margin: 0 8px;
  flex-shrink: 0;
  background: linear-gradient(${props => props.theme.brand?.gradient?.primary});
  background-repeat: no-repeat;
  background-size: ${props => props.length * 30}px 30px;
  background-position: -${props => (props.number - 1) * 30}px top;
  mask-image: url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M30 0H0V30H30V0ZM15 25C20.5228 25 25 20.5228 25 15C25 9.47715 20.5228 5 15 5C9.47715 5 5 9.47715 5 15C5 20.5228 9.47715 25 15 25Z' fill='black'/%3E%3C/svg%3E%0A");
  mask-position: 50% 50%;
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
`;

const BlockContentSection = ({
  section,
  idx,
  sectionCount,
  sectionNumber,
  children,
  variant = 'simple',
  theme: inheritedTheme,
  overrideTheme
}) => {
  const appliedTheme = (() => {
    if (!overrideTheme || overrideTheme === 'inherit') return inheritedTheme;
    if (overrideTheme === 'dark') return darkTheme;
    return lightTheme;
  })();

  return (
    <MuiThemeProvider theme={appliedTheme}>
      <ThemeProvider theme={appliedTheme}>
        <section data-swiftype-index="true">
          <ScrollElement
            name={_.kebabCase(section.heading)}
            style={{ scrollMarginTop: '50px' }}
          >
            {variant === 'simple' && (
              <>
                {section.heading && (
                  <>
                    {idx !== 0 && <PaddedDivider />}
                    <BlockContentTypography
                      variant="body2"
                      dangerouslySetInnerHTML={{
                        __html: `<h2>${section.heading}</h2>`
                      }}
                      {...section?.$?.heading}
                    />
                  </>
                )}
                {children}
              </>
            )}
            {variant === 'bold' && (
              <>
                <PaddedDivider spacing={4} />
                <Container>
                  <Grid container spacing={24}>
                    {section.heading && (
                      <Grid item xs={12} md={3}>
                        <BoldHeading>
                          <Typography variant="overline" gutterBottom>
                            {String(sectionNumber).padStart(2, 0)}.
                          </Typography>
                          <Typography variant="h2" {...section?.$?.heading}>
                            {section.heading}
                          </Typography>
                        </BoldHeading>
                      </Grid>
                    )}
                    <Grid item xs={12} md={section.heading ? 9 : 12}>
                      <PaddedDivider spacing={1} hide />
                      {children}
                    </Grid>
                  </Grid>
                </Container>
              </>
            )}
            {variant === 'accented' && (
              <>
                <PaddedDivider spacing={4} />
                <Container>
                  {section.heading && (
                    <BoldHeading>
                      <Typography
                        variant="h3"
                        component="h2"
                        {...section?.$?.heading}
                      >
                        {section.heading}
                      </Typography>
                    </BoldHeading>
                  )}
                  {children}
                </Container>
              </>
            )}

            {variant === 'timeline' && (
              <>
                <PaddedDivider spacing={4} />
                <TimelineContainer>
                  {section.heading && (
                    <TimelineHeading>
                      <Typography variant="overline" gutterBottom>
                        {String(sectionNumber).padStart(2, 0)}
                      </Typography>
                      <LabelCircle
                        number={sectionNumber}
                        length={sectionCount}
                      />
                      <Typography
                        variant="h5"
                        component="h2"
                        {...section?.$?.heading}
                      >
                        {section.heading}
                      </Typography>
                    </TimelineHeading>
                  )}
                  {children}
                </TimelineContainer>
              </>
            )}
          </ScrollElement>
        </section>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

export default withTheme()(BlockContentSection);
